import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandHeader from "@/components/BrandHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const REC_STYLES = {
  PROCEED: "bg-[#2F3E2F] text-white",
  PROCEED_WITH_CAUTION: "bg-[#D98032] text-white",
  SECOND_INTERVIEW: "bg-[#A64D2E] text-white",
  DECLINE: "bg-[#5E5C58] text-white",
};

export default function Admin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { logout, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/submissions`);
        setItems(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen">
      <BrandHeader minimal />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-3">
              Admin · Submissions
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">
              The people coming home.
            </h1>
            <p className="mt-3 text-[var(--tt-muted)] max-w-2xl">
              Every onboarding response, with the AI verdict at a glance.
              Click into one to read the full conversation.
            </p>
          </div>
          <Link
            to="/"
            data-testid="admin-back-home"
            className="text-xs uppercase tracking-[0.2em] text-[var(--tt-muted)] hover:text-[var(--tt-primary)] transition-colors"
          >
            ← Public Form
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="ml-4 text-xs uppercase tracking-[0.2em] text-[var(--tt-muted)] hover:text-[var(--tt-primary)] transition-colors"
          >
            Sign out · {user?.email}
          </button>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="p-10 rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] text-[var(--tt-muted)]" data-testid="admin-loading">
              Loading submissions…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-10 text-center" data-testid="admin-empty">
              <img
                src="https://images.unsplash.com/photo-1697503124876-ebaaec451905?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt=""
                className="mx-auto rounded-xl mb-6 max-h-56 object-cover w-full max-w-md"
              />
              <h3 className="font-display text-xl font-semibold">No submissions yet</h3>
              <p className="mt-2 text-[var(--tt-muted)]">
                Share the public link and they'll start arriving here.
              </p>
            </div>
          ) : (
            <div className="rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] overflow-hidden" data-testid="admin-list">
              <table className="w-full text-left">
                <thead className="bg-[var(--tt-surface-2)] text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">
                  <tr>
                    <th className="px-6 py-4">Candidate</th>
                    <th className="px-6 py-4 hidden sm:table-cell">Role</th>
                    <th className="px-6 py-4 hidden lg:table-cell">Headline</th>
                    <th className="px-6 py-4">Fit</th>
                    <th className="px-6 py-4">Verdict</th>
                    <th className="px-6 py-4 hidden md:table-cell">Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t border-[var(--tt-border)] hover:bg-[var(--tt-bg)] transition-colors"
                    >
                      <td className="px-6 py-4">
                        <Link
                          to={`/admin/${s.id}`}
                          data-testid={`row-${s.id}`}
                          className="font-display font-semibold text-[var(--tt-text)] hover:text-[var(--tt-primary)]"
                        >
                          {s.full_name}
                        </Link>
                        <div className="text-xs text-[var(--tt-muted)] mt-0.5">{s.email}</div>
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell text-sm">{s.role_applied}</td>
                      <td className="px-6 py-4 hidden lg:table-cell text-sm text-[var(--tt-muted)] max-w-sm">
                        {s.headline || "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-display text-xl font-bold text-[var(--tt-primary)]">
                          {typeof s.overall_fit_score === "number" ? s.overall_fit_score : "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {s.recommendation ? (
                          <span
                            className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${
                              REC_STYLES[s.recommendation] || "bg-[var(--tt-surface-2)] text-[var(--tt-text)]"
                            }`}
                          >
                            {String(s.recommendation).replace(/_/g, " ")}
                          </span>
                        ) : (
                          <span className="text-xs text-[var(--tt-muted)]">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell text-sm text-[var(--tt-muted)]">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
